import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, ShoppingBag, Users, DollarSign,
  BarChart2, RefreshCw, Package, Calendar,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import IndiaVisitorMap from "@/components/IndiaVisitorMap";

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { label: "Today",   value: "today" },
  { label: "7 days",  value: "7d" },
  { label: "30 days", value: "30d" },
  { label: "90 days", value: "90d" },
  { label: "1 year",  value: "1y" },
  { label: "Custom",  value: "custom" },
];

const STATUS_COLORS = {
  Placed:            "#fb923c",
  Accepted:          "#38bdf8",
  Packed:            "#818cf8",
  Shipped:           "#34d399",
  Out_for_delivery:  "#fbbf24",
  Delivered:         "#4ade80",
  Cancelled:         "#f87171",
  Rejected:          "#f87171",
};

const PIE_PALETTE = ["#f97316","#38bdf8","#818cf8","#4ade80","#fbbf24","#f87171","#a78bfa","#2dd4bf"];

function fmt(n) {
  if (n >= 10000000) return `₹${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n/1000).toFixed(1)}K`;
  return `₹${Number(n).toFixed(0)}`;
}

function fmtDate(str, range) {
  const d = new Date(str);
  if (range === "1y") return d.toLocaleString("en-IN", { month: "short", day: "numeric" });
  return d.toLocaleString("en-IN", { month: "short", day: "numeric" });
}

/* ── KPI card ─────────────────────────────────────────── */
function KPICard({ icon: Icon, label, value, sub, change, color }) {
  const up = change >= 0;
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-black text-slate-800 leading-none">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
          {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {up ? "+" : ""}{change}% vs prev period
        </div>
      )}
    </div>
  );
}

/* ── Custom tooltip for area chart ─────────────────────── */
function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-bold text-slate-700 mb-1">{label}</p>
      <p className="text-orange-600 font-semibold">{fmt(payload[0]?.value || 0)}</p>
      <p className="text-slate-500">{payload[1]?.value || 0} orders</p>
    </div>
  );
}

/* ── Status pie label ───────────────────────────────────── */
function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 700 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function todayStr() { return new Date().toISOString().split("T")[0]; }

export default function AnalyticsPage() {
  useAuth();
  const [range, setRange]             = useState("30d");
  const [customStart, setCustomStart] = useState(todayStr());
  const [customEnd,   setCustomEnd]   = useState(todayStr());
  const [showCustom, setShowCustom]   = useState(false);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async (r, cs, ce) => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/api/admin/analytics?period=${r}`;
      if (r === "custom") url += `&start_date=${cs}&end_date=${ce}`;
      const token = localStorage.getItem("it_token");
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      setError("Failed to load analytics data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range, customStart, customEnd); }, []); // eslint-disable-line

  const handleRange = (r) => {
    setRange(r);
    setShowCustom(r === "custom");
    if (r !== "custom") load(r, customStart, customEnd);
  };

  const applyCustom = () => load("custom", customStart, customEnd);

  const k = data?.kpis || {};
  const daily = (data?.daily || []).filter((_, i) => {
    // For 1y, sample every 3 days to avoid crowded x-axis
    if (range === "1y") return i % 3 === 0;
    if (range === "90d") return i % 2 === 0;
    return true;
  });
  const statuses = data?.status_breakdown || [];
  const methods  = data?.payment_methods  || [];
  const products = data?.top_products     || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center">
            <BarChart2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Analytics</h1>
            <p className="text-xs text-slate-400">Orders, revenue & customer insights</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* Range pills */}
          <div className="flex flex-wrap bg-slate-100 rounded-xl p-1 gap-1" data-testid="analytics-range-selector">
            {RANGES.map(r => (
              <button key={r.value} onClick={() => handleRange(r.value)}
                data-testid={`range-${r.value}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  range === r.value
                    ? "bg-orange-500 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}>
                {r.label}
              </button>
            ))}
            <button onClick={() => load(range, customStart, customEnd)} disabled={loading}
              className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors">
              <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {/* Custom date picker */}
          {showCustom && (
            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-white border border-orange-200 rounded-xl shadow-sm">
              <Calendar className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="text-xs text-slate-500 font-medium">From</span>
              <input type="date" value={customStart} max={customEnd}
                onChange={e => setCustomStart(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-orange-400" />
              <span className="text-xs text-slate-500 font-medium">To</span>
              <input type="date" value={customEnd} min={customStart} max={todayStr()}
                onChange={e => setCustomEnd(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-orange-400" />
              <button onClick={applyCustom}
                className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600">
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="m-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-7 h-7 text-orange-500 animate-spin" />
            <p className="text-sm text-slate-500">Loading analytics…</p>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-6" data-testid="analytics-dashboard">

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kpi-cards">
            <KPICard
              icon={DollarSign} label="Revenue" color="bg-orange-500"
              value={fmt(k.revenue || 0)}
              sub="from paid orders"
              change={k.revenue_change}
            />
            <KPICard
              icon={ShoppingBag} label="Orders" color="bg-sky-500"
              value={(k.orders || 0).toLocaleString("en-IN")}
              sub="in selected period"
              change={k.orders_change}
            />
            <KPICard
              icon={TrendingUp} label="Avg Order Value" color="bg-violet-500"
              value={fmt(k.avg_order_value || 0)}
              sub="per paid order"
              change={k.aov_change}
            />
            <KPICard
              icon={Users} label="Total Customers" color="bg-emerald-500"
              value={(k.total_customers || 0).toLocaleString("en-IN")}
              sub={`+${k.new_customers || 0} new this period`}
            />
          </div>

          {/* ── Revenue Area Chart ── */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="revenue-chart">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-sm font-bold text-slate-800">Revenue & Orders over time</h2>
                <p className="text-xs text-slate-400 mt-0.5">Paid order revenue per day</p>
              </div>
              <span className="text-xs font-bold text-orange-500 bg-orange-50 px-2.5 py-1 rounded-lg">
                {fmt(k.revenue || 0)} total
              </span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#f97316" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickFormatter={s => fmtDate(s, range)} interval="preserveStartEnd" />
                <YAxis yAxisId="rev" tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickFormatter={v => fmt(v)} width={60} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} width={30} />
                <Tooltip content={<RevenueTooltip />} />
                <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke="#f97316"
                  strokeWidth={2.5} fill="url(#revGrad)" dot={false} name="Revenue" />
                <Bar yAxisId="cnt" dataKey="orders" fill="#e0f2fe" radius={[3,3,0,0]} name="Orders" barSize={6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* ── Status + Payment ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Order Status donut */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="status-chart">
              <h2 className="text-sm font-bold text-slate-800 mb-1">Order Status</h2>
              <p className="text-xs text-slate-400 mb-4">Breakdown of orders in this period</p>
              {statuses.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-sm text-slate-400">No orders in this period</div>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="55%" height={200}>
                    <PieChart>
                      <Pie data={statuses} dataKey="count" nameKey="status"
                        cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                        labelLine={false} label={PieLabel}>
                        {statuses.map((_, i) => (
                          <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} orders`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {statuses.map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ background: PIE_PALETTE[i % PIE_PALETTE.length] }} />
                          <span className="text-xs text-slate-600">{s.status}</span>
                        </div>
                        <span className="text-xs font-bold text-slate-700">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Payment Methods bar */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="payment-chart">
              <h2 className="text-sm font-bold text-slate-800 mb-1">Payment Methods</h2>
              <p className="text-xs text-slate-400 mb-4">Orders by payment type in this period</p>
              {methods.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-sm text-slate-400">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={methods} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis type="category" dataKey="method" tick={{ fontSize: 11, fill: "#64748b" }} width={70} />
                    <Tooltip formatter={(v, n) => [n === "count" ? `${v} orders` : fmt(v), n === "count" ? "Orders" : "Revenue"]} />
                    <Bar dataKey="count" fill="#fb923c" radius={[0,5,5,0]} barSize={18} name="count">
                      {methods.map((_, i) => (
                        <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ── Top Products ── */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="top-products">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-800">Top Products</h2>
                <p className="text-xs text-slate-400 mt-0.5">By revenue in selected period</p>
              </div>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            {products.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-slate-400">No order data in this period</div>
            ) : (
              <div className="divide-y divide-slate-50">
                <div className="grid grid-cols-12 gap-4 px-5 py-2.5 bg-slate-50">
                  <span className="col-span-1 text-[11px] font-bold text-slate-400 uppercase">#</span>
                  <span className="col-span-5 text-[11px] font-bold text-slate-400 uppercase">Product</span>
                  <span className="col-span-2 text-[11px] font-bold text-slate-400 uppercase text-right">Qty</span>
                  <span className="col-span-2 text-[11px] font-bold text-slate-400 uppercase text-right">Revenue</span>
                  <span className="col-span-2 text-[11px] font-bold text-slate-400 uppercase text-right">Avg</span>
                </div>
                {products.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-4 px-5 py-3 items-center hover:bg-orange-50/30">
                    <span className="col-span-1 text-xs font-bold text-slate-400">{i + 1}</span>
                    <div className="col-span-5 flex items-center gap-3">
                      {p.image ? <img src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                        : <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><Package className="w-4 h-4 text-slate-300" /></div>}
                      <span className="text-sm font-medium text-slate-700 line-clamp-2 leading-tight">{p.name}</span>
                    </div>
                    <span className="col-span-2 text-sm font-semibold text-slate-700 text-right">{p.qty.toLocaleString("en-IN")}</span>
                    <span className="col-span-2 text-sm font-bold text-orange-600 text-right">{fmt(p.revenue)}</span>
                    <span className="col-span-2 text-sm text-slate-500 text-right">{p.qty > 0 ? fmt(p.revenue / p.qty) : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Visitor Breakdowns: Device / Browser / Location ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Device */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="device-chart">
              <h2 className="text-sm font-bold text-slate-800 mb-1">Devices</h2>
              <p className="text-xs text-slate-400 mb-4">Visits by device type</p>
              {!data?.has_visitor_data ? (
                <div className="flex items-center justify-center h-40 text-sm text-slate-400">No visitor data yet</div>
              ) : (
                <div className="space-y-2.5">
                  {(data.device_breakdown || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-16 flex-shrink-0">{d.name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: PIE_PALETTE[i % PIE_PALETTE.length] }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-10 text-right">{d.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Browser */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="browser-chart">
              <h2 className="text-sm font-bold text-slate-800 mb-1">Browsers</h2>
              <p className="text-xs text-slate-400 mb-4">Visits by browser</p>
              {!data?.has_visitor_data ? (
                <div className="flex items-center justify-center h-40 text-sm text-slate-400">No visitor data yet</div>
              ) : (
                <div className="space-y-2.5">
                  {(data.browser_breakdown || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-24 flex-shrink-0 truncate">{d.name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: PIE_PALETTE[i % PIE_PALETTE.length] }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-10 text-right">{d.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Location */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="location-chart">
              <h2 className="text-sm font-bold text-slate-800 mb-1">Locations</h2>
              <p className="text-xs text-slate-400 mb-4">Unique visitors by state</p>
              <IndiaVisitorMap locations={(data?.location_breakdown || []).map(d => ({ state: d.name, count: d.value, pct: d.pct }))} />
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
